import { Group, Loader, Text } from "@mantine/core";

// What a run is doing and what it wrote, shared by /python and /javascript because an interpreter behind a page is the
// same shape of thing whichever language it speaks: it starts, it writes faster than the page can draw, and it ends.

export type Run =
  | { state: "idle" }
  | { state: "starting" }
  | { state: "running" }
  | { state: "finished"; seconds: number }
  | { state: "stopped" }
  | { state: "failed"; message: string };

export const IDLE: Run = { state: "idle" };

export function isWorking(run: Run): boolean {
  return run.state === "starting" || run.state === "running";
}

// Starting is its own word because the first run fetches an interpreter, and a spinner alone would not say why.
export function RunStatus({ run, starting }: { run: Run; starting: string }) {
  switch (run.state) {
    case "idle":
      return null;
    case "starting":
    case "running":
      return (
        <Group gap="xs" wrap="nowrap">
          <Loader size="xs" />
          <Text size="sm" c="dimmed">{run.state === "starting" ? starting : "Running…"}</Text>
        </Group>
      );
    case "finished":
      return <Text size="sm" c="dimmed">Finished in {run.seconds.toFixed(2)}s</Text>;
    case "stopped":
      return <Text size="sm" c="dimmed">Stopped</Text>;
    case "failed":
      return <Text size="sm" c="red">{run.message}</Text>;
  }
}

// What a run wrote since the last post, and whether the interpreter had to drop any of it to keep up.
export interface Chunk {
  text: string;
  dropped: boolean;
}

export const NOTHING_HELD: Chunk = { text: "", dropped: false };

// One redraw every this often at most: a `<pre>` this size is not worth laying out for every chunk that arrives.
export const DRAW_DELAY = 100;

// The scrollback, past which the oldest goes. A panel nobody can scroll to the top of is not made better by more.
// It bounds one block of a session as it bounds a whole script, since a loop at a prompt prints like any other.
export const MAX_OUTPUT = 128 * 1024;

export const DROPPED_NOTE = "… earlier output dropped\n";

// What a run came to, spelled where the output ends: the time it took, and how much of it there was to read.
export function runStats(seconds: number, output: string | null, dropped: boolean): string {
  const written = output ?? "";
  const finished = `Finished in ${seconds.toFixed(2)}s`;
  if (!written) return `${finished} · no output`;

  // A trailing newline ends the last line rather than opening another, which is the line a terminal would not count.
  const lines = written.split("\n").length - (written.endsWith("\n") ? 1 : 0);
  return `${finished} · ${lines} ${lines === 1 ? "line" : "lines"}${dropped ? " kept" : ""}`;
}

export function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
